import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { EditorState, Prec, Compartment } from '@codemirror/state';
import { sql } from '@codemirror/lang-sql';
import { format } from 'sql-formatter';
import { buildFormatOptions } from '../utils/sqlFormat';
import { parseStatements } from '../utils/sqlBinds';
import { statementAtCursor } from '../utils/statementAtCursor';
import { minimapLineHeight, minimapViewport, minimapScrollTarget } from '../utils/minimapScale';
import { listenDrag } from '../utils/dragListen';
import { parseBookmarks, bookmarkName, BOOKMARK_PREFIX } from '../utils/sqlBookmarks';
import { EditorView, keymap, lineNumbers, highlightActiveLineGutter, highlightActiveLine, drawSelection, scrollPastEnd, Decoration, ViewPlugin, GutterMarker, gutter, gutterLineClass, rectangularSelection, crosshairCursor } from '@codemirror/view';
import { StateField, StateEffect } from '@codemirror/state';
import { defaultKeymap, history, historyKeymap, toggleComment, indentMore, indentLess, undo, redo, insertNewlineAndIndent } from '@codemirror/commands';
import { syntaxHighlighting, defaultHighlightStyle, indentOnInput, bracketMatching, indentUnit } from '@codemirror/language';
import { autocompletion, completionKeymap, closeBrackets, acceptCompletion, completionStatus, snippetCompletion } from '@codemirror/autocomplete';
import { loadSnippets } from '../utils/sqlSnippets';
import { parseTableRefs, resolveRef } from '../utils/sqlTableRefs';
import { getColumns, peekColumns } from '../services/columnCache';
import { search, searchKeymap, openSearchPanel, searchPanelOpen } from '@codemirror/search';
import { openReplacePanel } from '../utils/editorSearch';
import { qualifiedNameAtCursor } from '../utils/sqlIdentifier';
import { useSelector } from 'react-redux';
import { lookupValue as lookupUserComment, activeFor as userCommentActiveFor } from '../services/userComments';
import { unquoteValue, columnBeforeValue } from '../utils/userCommentText';
import { syntaxTree } from "@codemirror/language";
import './SQLEditor.css';
import PropTypes from 'prop-types';
import { RangeSet } from '@codemirror/state';

import { toCssPx } from '../utils/appZoom';
// ============================================
// 책갈피 — `--@ 이름` 주석 줄 (utils/sqlBookmarks.js)
// ============================================
// 예전에는 줄 번호를 누르면 메모리에만 있는 ● 를 찍었다(저장되지 않았다). 이제 책갈피는 문서의 글자이고,
// 줄 번호를 누르면 그 위에 `--@ ` 줄을 넣거나(이름을 바로 칠 수 있게 커서를 둔다) 책갈피 줄이면 지운다.

// ─── 실행된 쿼리 ↔ 결과탭 연결 표시 (line number 옆 gutter) ─────────────────
// 각 statement 의 첫 줄에 점프 아이콘을 그려서, 클릭하면 그 statement 로 만들어진
// 결과탭이 활성화. statement 의 sourceSql 매칭으로 mark 위치 결정 → useEffect 에서
// 외부 props (linkedResultTabs) 와 statements 변화에 따라 effect dispatch.

class LinkedResultMarker extends GutterMarker {
  constructor(tabId, tabName) {
    super();
    this.tabId = tabId;
    this.tabName = tabName;
  }
  toDOM() {
    const el = document.createElement('span');
    el.className = 'cm-linked-result-marker';
    el.title = this.tabId
      ? i18n.t('editor:linkedResult.gotoTab', { tabName: this.tabName })
      : '';
    if (this.tabId) el.dataset.linkedTabId = this.tabId;
    // Spacer (tabId 없음) 면 비표시 — 너비만 차지하고 아이콘은 안 그림.
    if (this.tabId) {
      // 결과 그리드 모양 — 통합 헤더의 결과 탭 아이콘(BiTable) 과 동일한 시각.
      el.innerHTML = '<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true"><path d="M21 4H3a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h18a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1zM4 6h6v3H4V6zm8 0h8v3h-8V6zM4 11h6v3H4v-3zm8 0h8v3h-8v-3zM4 16h6v3H4v-3zm8 0h8v3h-8v-3z"/></svg>';
    }
    return el;
  }
  eq(other) { return other instanceof LinkedResultMarker && other.tabId === this.tabId; }
}

/**
 * Tab — 자동 완성이 떠 있으면 그 항목을 넣고, 선택이 있으면 선택한 줄을 한 단계 들여쓰고(indentUnit = 공백 4칸),
 * 선택이 없으면 커서 자리에 공백 4칸. 예전에는 선택을 공백으로 바꿔 넣어 고른 글이 사라졌다(2026-10-21 수정).
 */
export function editorTabKey(view) {
    if (completionStatus(view.state) === 'active') {
        acceptCompletion(view);
        return true;
    }
    if (view.state.selection.ranges.some(r => !r.empty)) return indentMore(view);
    view.dispatch(view.state.replaceSelection('    '));
    return true;
}

/** Shift+Tab — 선택한 줄(없으면 커서가 있는 줄)을 한 단계 내어쓴다. 늘 true — false 면 브라우저가 포커스를 편집기 밖으로 옮긴다 */
export function editorShiftTabKey(view) {
    indentLess(view);
    return true;
}

// gutter 폭 고정용 spacer — 항상 같은 크기의 빈 marker 를 기준으로 너비 산정.
const linkedResultSpacer = new LinkedResultMarker(null, null);

const setLinkedResultMarkersEffect = StateEffect.define();

const linkedResultField = StateField.define({
  create() { return RangeSet.empty; },
  update(marks, tr) {
    marks = marks.map(tr.changes);
    for (const e of tr.effects) {
      if (e.is(setLinkedResultMarkersEffect)) {
        const list = e.value || [];
        const newMarks = list.map(({ pos, tabId, tabName }) =>
          new LinkedResultMarker(tabId, tabName).range(pos)
        );
        return RangeSet.of(newMarks, true);
      }
    }
    return marks;
  },
});

function createLinkedResultGutter(getOnActivate) {
  return gutter({
    class: 'cm-linked-result-gutter',
    markers: v => v.state.field(linkedResultField),
    initialSpacer: () => linkedResultSpacer, // marker 추가/제거에도 gutter 너비 일정
    domEventHandlers: {
      mousedown(view, line) {
        const marks = view.state.field(linkedResultField);
        let target = null;
        marks.between(line.from, line.from + 1, (from, to, value) => {
          if (value instanceof LinkedResultMarker) target = value.tabId;
        });
        if (target) {
          const onActivate = getOnActivate();
          if (typeof onActivate === 'function') onActivate(target);
          return true;
        }
        return false;
      },
    },
  });
}

// ─── statement 경계 시각 표시 (각 statement 첫 줄의 gutter element 에 class 부여) ────
// Decoration.line 은 본문 .cm-line 에만 적용되어 가로 전체 line 에 border 가 그려졌음.
// gutterLineClass + GutterMarker.elementClass 를 쓰면 gutter element 에만 class 가 붙음.
const statementSepMarker = new (class extends GutterMarker {
  get elementClass() { return 'cm-statement-separator'; }
})();
const setStatementSeparatorsEffect = StateEffect.define();
const statementSeparatorField = StateField.define({
  create() { return RangeSet.empty; },
  update(marks, tr) {
    marks = marks.map(tr.changes);
    for (const e of tr.effects) {
      if (e.is(setStatementSeparatorsEffect)) {
        const positions = e.value || [];
        const newMarks = positions.map(pos => statementSepMarker.range(pos));
        return RangeSet.of(newMarks, true);
      }
    }
    return marks;
  },
  provide: f => gutterLineClass.from(f),
});

const bookmarkMarker = new class extends GutterMarker {
  toDOM() {
    const el = document.createElement('span');
    el.textContent = '\u25CF'; // ●
    el.className = 'cm-bookmark-marker';
    return el;
  }
};

// 책갈피 줄의 시작 위치들 — 문서에서 **계산**한다(따로 기억하지 않는다). 문서가 바뀔 때만 다시 센다.
function computeBookmarkMarks(doc) {
  const marks = [];
  const text = doc.toString();
  if (text.indexOf('--@') < 0) return RangeSet.empty;
  for (const bm of parseBookmarks(text)) marks.push(bookmarkMarker.range(doc.line(bm.line).from));
  return RangeSet.of(marks, true);
}

const bookmarkField = StateField.define({
  create(state) { return computeBookmarkMarks(state.doc); },
  update(marks, tr) { return tr.docChanged ? computeBookmarkMarks(tr.state.doc) : marks; },
});

/**
 * 줄 번호·● 칸을 눌렀을 때 — 책갈피 줄이면 지우고, 아니면 그 위에 `--@ ` 를 넣는다.
 * 읽기 전용 편집기(결과 탭 옆의 사본 등)에서는 아무것도 하지 않는다.
 */
function toggleBookmarkAt(view, lineBlock) {
  if (view.state.readOnly) return true;
  const line = view.state.doc.lineAt(lineBlock.from);
  if (bookmarkName(line.text) !== null) {
    // 줄째 지운다 — 다음 줄바꿈까지(마지막 줄이면 앞 줄바꿈까지)
    const doc = view.state.doc;
    const from = line.number < doc.lines ? line.from : Math.max(0, line.from - 1);
    const to = line.number < doc.lines ? line.to + 1 : line.to;
    view.dispatch({ changes: { from, to, insert: '' }, userEvent: 'delete.bookmark' });
    return true;
  }
  const indent = (/^[ \t]*/.exec(line.text) || [''])[0];
  const insert = `${indent}${BOOKMARK_PREFIX}\n`;
  const cursor = line.from + indent.length + BOOKMARK_PREFIX.length;
  view.dispatch({
    changes: { from: line.from, insert },
    selection: { anchor: cursor },
    scrollIntoView: true,
    userEvent: 'input.bookmark',
  });
  view.focus();
  return true;
}

function createBookmarkGutter() {
  return gutter({
    class: 'cm-bookmark-gutter',
    markers: v => v.state.field(bookmarkField),
    initialSpacer: () => bookmarkMarker,
    domEventHandlers: {
      mousedown(view, line) {
        return toggleBookmarkAt(view, line);
      },
    },
  });
}

// 책갈피 라인 하이라이트 데코레이션
const bookmarkLineHighlight = Decoration.line({ class: 'cm-bookmark-line' });

const bookmarkLineDecorations = EditorView.decorations.compute([bookmarkField], state => {
  const marks = state.field(bookmarkField);
  const decs = [];
  const iter = marks.iter();
  while (iter.value) {
    decs.push(bookmarkLineHighlight.range(iter.from));
    iter.next();
  }
  return Decoration.set(decs, true);
});

// 외부 하이라이트 데코레이션 (실행계획↔에디터 연동)
const setHighlightsEffect = StateEffect.define();
const highlightField = StateField.define({
    create() { return Decoration.none; },
    update(decorations, tr) {
        for (const e of tr.effects) {
            if (e.is(setHighlightsEffect)) {
                return e.value.length > 0 ? RangeSet.of(e.value) : Decoration.none;
            }
        }
        return decorations.map(tr.changes);
    },
    provide: f => EditorView.decorations.from(f),
});


// 읽기 전용 = 상태 잠금 + 편집 불가. **Compartment 에 담아 나중에 바꿀 수 있게** 한다(아래 effect).
const readOnlyExtensions = (ro) => (ro ? [EditorState.readOnly.of(true), EditorView.editable.of(false)] : []);

const SQLEditor = ({ query, setQuery, onExecute, onExecuteWithContext, onExecutePlan, onViewSchemaInfoByEditor, schemas, editorRef, onFocus, onBlur, onNewTab, bindVariables = {}, onEditBindVariables, tableComments = {}, columnComments = {}, highlightRanges = [], readOnly = false, columnSelection = false, linkedResultTabs = [], onActivateResultTab = null, onFind = null, onFindNav = null, watermark = '', minimap = true, autocomplete = true, showComments = true, connectionId = null, dbType = 'oracle', usrid = null, defaultSchema = '' }) => {
    const { t } = useTranslation();
    const editorRefInternal = useRef(null);
    const viewRef = useRef(null);
    /** CodeMirror 뷰가 만들어졌음을 알리는 신호 (값 자체는 뜻이 없다 — 바뀌는 것이 요점이다). */
    const [viewReady, setViewReady] = useState(0);
    // 편집기를 다시 만들지 않고 읽기 전용만 켜고 끈다
    const readOnlyCompartmentRef = useRef(null);
    if (readOnlyCompartmentRef.current === null) readOnlyCompartmentRef.current = new Compartment();
    const schemasRef = useRef(schemas);  // schemas를 추적하기 위한 ref
    const columnSelectionRef = useRef(columnSelection); // 컬럼 선택 모드 상태 추적 (eventFilter가 읽음)
    // 설정의 「추천 단어(자동완성)」·「주석 보기」 — 에디터는 한 번만 만들어지므로 ref 로 읽어야 바꾸는 즉시 듣는다.
    // 예전에는 이 둘이 에디터까지 오지 않아, 설정을 바꿔도 아무 일도 없었다(「즉시 적용」 요청의 원인, 2026-10-02)
    const autocompleteRef = useRef(autocomplete);
    autocompleteRef.current = autocomplete;
    // 컬럼 자동완성이 컬럼을 받아 올 연결 — ref 로 두어 completion 함수가 늘 최신을 본다
    const connRef = useRef({ connectionId, dbType, usrid, defaultSchema });
    connRef.current = { connectionId, dbType, usrid, defaultSchema };
    const columnCommentsRef = useRef(columnComments);
    columnCommentsRef.current = columnComments;
    const showCommentsRef = useRef(showComments);
    showCommentsRef.current = showComments;
    // **편집기는 한 번만 만들어지므로 내용 변경은 늘 최신 setQuery 로 보낸다**(2026-10-26 신고 — 프로젝트와 개별 스크립트의 첫 탭이 섞였다).
    // 처음 만들 때의 setQuery 를 붙잡고 있으면, 프로젝트를 바꾸는 순간 잠깐 다른 탭을 가리키던 그 함수로 계속 써서 친 글이 엉뚱한 탭에 들어갔다
    const setQueryRef = useRef(setQuery);
    setQueryRef.current = setQuery;
    const onActivateResultTabRef = useRef(onActivateResultTab); // 한번 생성된 gutter 가 최신 핸들러 참조
    const onFindRef = useRef(onFind);       // 키맵은 한 번만 만들어지므로 최신 핸들러를 ref 로 본다
    const onFindNavRef = useRef(onFindNav);
    // EditorView 의 keymap 은 mount 시 한 번만 빌드되므로 그 안의 handler 들이 stale closure 가 됨.
    // ref 로 최신 useCallback 을 가리켜서 keymap 은 항상 최신을 호출하게 함.
    const handleExecuteInternalRef = useRef(null);
    const handleExecutePlanInternalRef = useRef(null);
    const [contextMenu, setContextMenu] = useState(null);
    const [isBindVariablesCollapsed, setIsBindVariablesCollapsed] = useState(true); // 기본값: 접힌 상태
    const [commentTooltip, setCommentTooltip] = useState(null); // { x, y, text }
    const [watermarkStyle, setWatermarkStyle] = useState({}); // 워터마크 동적 위치
    const containerRef = useRef(null); // 컨테이너 ref 추가
    const skipNextUpdate = useRef(false);

    // schemas 변경 시 ref 업데이트
    useEffect(() => {
        schemasRef.current = schemas;
    }, [schemas]);

    // 컬럼 선택 모드 상태 동기화 (ref로 eventFilter 함수가 항상 최신 값 참조)
    useEffect(() => {
        columnSelectionRef.current = columnSelection;
    }, [columnSelection]);

    // 바인드 변수 섹션 토글 함수
    const toggleBindVariablesSection = useCallback(() => {
        setIsBindVariablesCollapsed(prev => !prev);
    }, []);

    /**
     * 테이블 참조의 컬럼 — 캐시에 없으면 받아 온다(services/columnCache). 스키마가 안 적혀 있으면 탐색기에 든 스키마 중
     * 그 테이블을 가진 것을 고른다. 주석은 탐색기가 미리 받아 둔 columnComments 로 채운다.
     */
    const columnsForRef = async (ref, { sync = false } = {}) => {
        const { connectionId: cid, dbType: dt, usrid: uid, defaultSchema: ds } = connRef.current;
        if (!cid || !ref) return null;
        let schema = ref.schema;
        if (!schema) {
            const owner = (schemasRef.current || []).find(sc =>
                (sc.tables || []).some(tb => String(tb.name).toUpperCase() === ref.table.toUpperCase())
                || (sc.views || []).some(v => String(v.name).toUpperCase() === ref.table.toUpperCase()));
            // 트리에 없으면 연결의 기본 스키마(탭 연결이 탐색기와 다를 때) → 그래도 없으면 트리의 첫 스키마
            // 연결의 기본 스키마는 사용자가 적은 그대로다('edu') — 딕셔너리는 대소문자를 접는다(Oracle 대문자, PG 소문자).
            // 화면 검증(2026-10-03)에서 OWNER = 'edu' 로 물어 0건이었다
            const fold = (x) => (x ? (dt === 'postgresql' ? String(x).toLowerCase() : String(x).toUpperCase()) : x);
            schema = owner ? owner.name : (fold(ds) || ((schemasRef.current || [])[0] || {}).name);
        }
        if (!schema) return null;
        const cols = sync ? peekColumns(cid, schema, ref.table) : await getColumns({ connectionId: cid, dbType: dt, schema, table: ref.table, usrid: uid });
        if (!cols) {
            // 동기 조회에서 없으면 뒤에서 받아 둔다 — 다음 글자부터 보인다
            if (sync) getColumns({ connectionId: cid, dbType: dt, schema, table: ref.table, usrid: uid });
            return null;
        }
        const cc = columnCommentsRef.current || {};
        const byTable = cc[`${schema}.${ref.table}`] || cc[ref.table] || {};
        return cols.map(c => ({ ...c, comment: c.comment || byTable[c.name] || null }));
    };
    const columnOption = (c, ref) => ({
        label: c.name,
        type: 'property',
        detail: [c.type, c.comment].filter(Boolean).join(' · ') || `${t('editor:autocomplete.column')} ${ref.alias || ref.table}`,
        boost: 60,
    });

    // 자동완성 override 함수 (schemasRef를 통해 항상 최신 schemas 참조)
    const completionOverride = async (context) => {
        if (!autocompleteRef.current) return null;
        // `별칭.` 또는 `스키마.` 뒤 — 그 테이블의 컬럼 / 그 스키마의 테이블만 (2026-10-03, 예전에는 테이블 이름까지만 됐다)
        const dotted = context.matchBefore(/[\p{L}_][\p{L}\p{N}_$#]*\.[\p{L}\p{N}_$#]*/u);
        if (dotted) {
            const m = /^([\p{L}_][\p{L}\p{N}_$#]*)\.([\p{L}\p{N}_$#]*)$/u.exec(dotted.text);
            if (m) {
                const [, qualifier, partial] = m;
                const from = dotted.from + qualifier.length + 1;
                const doc = context.state.doc.toString();
                const stmt = statementAtCursor(doc, context.pos);
                const ref = resolveRef(parseTableRefs(stmt ? stmt.text : doc), qualifier);
                let options = [];
                if (ref) {
                    const cols = await columnsForRef(ref);
                    options = (cols || []).map(c => columnOption(c, ref));
                } else {
                    const sc = (schemasRef.current || []).find(x => String(x.name).toUpperCase() === qualifier.toUpperCase());
                    if (sc) {
                        options = [
                            ...(sc.tables || []).map(tb => ({ label: tb.name, type: 'variable', detail: `[Table] ${sc.name}` })),
                            ...(sc.views || []).map(v => ({ label: v.name, type: 'variable', detail: `[View] ${sc.name}` })),
                        ];
                    }
                }
                const typed = partial.toUpperCase();
                const filtered = typed ? options.filter(o => o.label.toUpperCase().includes(typed)) : options;
                return filtered.length > 0 ? { from, options: filtered, validFor: /^[\p{L}\p{N}_$#]*$/u } : null;
            }
        }
        const wordInfo = context.matchBefore(/\w*/);
        if (!wordInfo) return null;

        const allKeywords = [
            { label: 'SELECT', type: 'keyword' },
            { label: 'FROM', type: 'keyword' },
            { label: 'WHERE', type: 'keyword' },
            { label: 'GROUP BY', type: 'keyword' },
            { label: 'ORDER BY', type: 'keyword' },
            { label: 'HAVING', type: 'keyword' },
            { label: 'JOIN', type: 'keyword' },
            { label: 'LEFT JOIN', type: 'keyword' },
            { label: 'RIGHT JOIN', type: 'keyword' },
            { label: 'INNER JOIN', type: 'keyword' },
        ];

        // 테이블명 및 뷰명 추출 (schemasRef를 통해 항상 최신 데이터 참조)
        const tableAndViewOptions = [];
        if (schemasRef.current && Array.isArray(schemasRef.current)) {
            schemasRef.current.forEach(schema => {
                if (schema.tables) {
                    schema.tables.forEach(table => {
                        tableAndViewOptions.push({
                            label: table.name,
                            type: 'variable',
                            detail: `[Table] ${schema.name}`
                        });
                    });
                }
                if (schema.views) {
                    schema.views.forEach(view => {
                        tableAndViewOptions.push({
                            label: view.name,
                            type: 'variable',
                            detail: `[View] ${schema.name}`
                        });
                    });
                }
            });
        }

        // SQL 템플릿(스니펫) — 약어를 치면 문형이 펼쳐진다. 키워드보다 위에 보이도록 boost.
        const snippetOptions = loadSnippets().map(sn => snippetCompletion(sn.template, {
            label: sn.label,
            detail: sn.detail || t('editor:autocomplete.snippet'),
            type: 'snippet',
            boost: 50,
        }));

        // 이 문장이 참조하는 테이블의 컬럼 — 받아 둔 것만(동기). 없으면 뒤에서 받아 둔다
        const columnOptions = [];
        try {
            const doc = context.state.doc.toString();
            const stmt = statementAtCursor(doc, context.pos);
            for (const ref of parseTableRefs(stmt ? stmt.text : doc)) {
                const cols = await columnsForRef(ref, { sync: true });
                (cols || []).forEach(c => columnOptions.push(columnOption(c, ref)));
            }
        } catch (_e) { /* 참조를 못 읽으면 컬럼 없이 */ }

        const allOptions = [...snippetOptions, ...columnOptions, ...allKeywords, ...tableAndViewOptions];

        if (wordInfo.text === "") {
            if (context.explicit) {
                return {
                    from: context.pos,
                    options: allOptions
                };
            }
            return null;
        }

        const typedText = wordInfo.text.toUpperCase();
        const filteredOptions = allOptions.filter(opt => {
            const label = opt.label.toUpperCase();
            // 스니펫: 약어를 끝까지 쳤을 때가 정작 필요한 순간이라 완전 일치도 남긴다.
            if (opt.type === 'snippet') return label.indexOf(typedText) === 0;
            return label.indexOf(typedText) !== -1 && label !== typedText;
        });

        return filteredOptions.length > 0 ? {
            from: wordInfo.from,
            options: filteredOptions
        } : null;
    };


    // 에디터의 실제 내용을 기반으로 statements 계산 (실시간 동기화).
    // useMemo 로 reference 안정화 — 매 render 마다 새 배열을 만들면 statements 를 deps 로
    // 쓰는 useEffect 가 폴링 tick 마다 발화해서 view.dispatch 가 반복되고 CodeMirror 의
    // measure loop 가 reset 됨 ("Measure loop restarted more than 5 times" 경고).
    const [currentEditorContent, setCurrentEditorContent] = useState(query);
    const statements = useMemo(
        () => parseStatements(currentEditorContent),
        [currentEditorContent]
    );

    // query prop 변경 시 currentEditorContent 동기화
    useEffect(() => {
        setCurrentEditorContent(query);
    }, [query]);

    // 결과탭 점프 핸들러 ref 동기화 — gutter 가 한번만 생성되므로 ref 로 최신값 참조.
    useEffect(() => {
        onActivateResultTabRef.current = onActivateResultTab;
        onFindRef.current = onFind;
        onFindNavRef.current = onFindNav;
    }, [onActivateResultTab]);

    // 각 statement 의 sourceSql 과 linkedResultTabs 매칭해서 marker 위치 계산 후 effect dispatch.
    // requestAnimationFrame 으로 defer + 직전 markers 와 동일하면 skip — CM 의 measure loop 와
    // React commit phase 가 충돌해 "Measure loop restarted more than 5 times" 경고 + 입력 지연 발생.
    //
    // **뷰가 새로 만들어지면 다시 그린다**(viewReady·뷰 식별을 키에 넣는다, 2026-10-07). 결과 영역을 창분리하면 편집기가
    // 다시 마운트되는데 이 효과는 뷰가 생기기 전에 한 번 돌고 끝나서(뷰 없음 → return), 다음 조회로 결과탭이 바뀔
    // 때까지 아이콘이 사라져 있었다. 그리고 「같은 키면 건너뛴다」 의 키를 RAF 가 그리기 **전에** 기록해, 그 RAF 가
    // 취소되면 영영 안 그렸다 — 키는 실제로 그린 뒤에 남긴다.
    const lastMarkersKeyRef = useRef('');
    const markersViewRef = useRef(null);
    useEffect(() => {
        if (readOnly) return;
        const view = viewRef.current;
        if (!view) return;
        if (!view.state.field(linkedResultField, false)) return;
        if (markersViewRef.current !== view) { markersViewRef.current = view; lastMarkersKeyRef.current = ''; }

        const tsOf = (id) => {
            const m = String(id || '').match(/_(\d{10,})_/);
            return m ? Number(m[1]) : 0;
        };
        const normalize = (s) => String(s || '')
            .replace(/--[^\n]*/g, '')   // 라인 주석 제거 (handleExecute 와 동일)
            .replace(/\s+/g, ' ')        // 모든 공백을 단일 스페이스로
            .trim();

        const markers = [];
        for (const stmt of statements) {
            const stmtNorm = normalize(stmt.text);
            if (!stmtNorm) continue;
            const matches = (linkedResultTabs || []).filter(t =>
                t && t.sourceSql && normalize(t.sourceSql) === stmtNorm
            );
            if (matches.length === 0) continue;
            // 동일 sql 로 만들어진 결과탭이 여러 개면 가장 최근 것 (id timestamp 큰 것).
            let best = matches[0];
            for (const t of matches) if (tsOf(t.id) > tsOf(best.id)) best = t;

            // statement.start 는 직전 ; 바로 뒤 (선행 공백 포함). 실제 콘텐츠 시작점 찾아서
            // 그 줄에 아이콘을 배치 (단순 ; 다음 빈 줄에 안 그리도록).
            const fullDoc = view.state.doc.toString();
            const segment = fullDoc.substring(stmt.start, stmt.end);
            const leadingWs = segment.length - segment.trimStart().length;
            const contentPos = Math.min(stmt.start + leadingWs, fullDoc.length - 1);
            const linePos = view.state.doc.lineAt(contentPos).from;
            markers.push({ pos: linePos, tabId: best.id, tabName: best.name || t('editor:resultTab.fallbackName') });
        }
        // 변경 없으면 dispatch skip — 같은 markers 를 매 render 마다 dispatch 하면 CM 이 measure 재시작.
        const key = markers.map(m => `${m.pos}:${m.tabId}`).join('|');
        if (key === lastMarkersKeyRef.current) return;
        const raf = requestAnimationFrame(() => {
            // RAF 시점에 view 가 unmount 됐을 수 있음 — 가드.
            if (viewRef.current === view && !view.destroyed) {
                view.dispatch({ effects: setLinkedResultMarkersEffect.of(markers) });
                lastMarkersKeyRef.current = key;
            }
        });
        return () => cancelAnimationFrame(raf);
    }, [linkedResultTabs, statements, currentEditorContent, readOnly, viewReady]);

    // statement 경계 표시 — 첫 statement 를 제외한 각 statement 의 첫 줄 line gutter 에
    // .cm-statement-separator 클래스 부여. gutterLineClass 를 통해 본문이 아닌 gutter
    // element 에만 적용되므로, line number 영역에서만 시각 단서가 그려짐.
    const lastSepPositionsKeyRef = useRef('');
    useEffect(() => {
        if (readOnly) return;
        const view = viewRef.current;
        if (!view) return;
        if (!view.state.field(statementSeparatorField, false)) return;

        const positions = [];
        const fullDoc = view.state.doc.toString();
        for (let i = 1; i < statements.length; i++) {
            const stmt = statements[i];
            const segment = fullDoc.substring(stmt.start, stmt.end);
            const leadingWs = segment.length - segment.trimStart().length;
            const contentPos = Math.min(stmt.start + leadingWs, Math.max(0, fullDoc.length - 1));
            const linePos = view.state.doc.lineAt(contentPos).from;
            positions.push(linePos);
        }
        const key = positions.join('|');
        if (key === lastSepPositionsKeyRef.current) return;
        lastSepPositionsKeyRef.current = key;
        const raf = requestAnimationFrame(() => {
            if (viewRef.current === view && !view.destroyed) {
                view.dispatch({ effects: setStatementSeparatorsEffect.of(positions) });
            }
        });
        return () => cancelAnimationFrame(raf);
    }, [statements, currentEditorContent, readOnly]);


    const handleContextMenuAction = useCallback(async (action) => {
        if (!viewRef.current) return;
        const view = viewRef.current;
        const selection = view.state.selection.main;
        const selectedText = view.state.doc.sliceString(selection.from, selection.to);

        let newText = selectedText;
        let replacementFrom = selection.from;
        let replacementTo = selection.to;

        switch (action) {
            case 'undo':
                // Undo 실행
                undo(view);
                setContextMenu(null);
                return;

            case 'redo':
                // Redo 실행
                redo(view);
                setContextMenu(null);
                return;

            case 'copy':
                // 선택된 텍스트를 클립보드에 복사
                if (selectedText) {
                    try {
                        await navigator.clipboard.writeText(selectedText);
                        console.log('✅ 복사 완료:', selectedText.length, '자');
                    } catch (error) {
                        console.error('❌ 복사 실패:', error);
                    }
                }
                setContextMenu(null);
                return;

            case 'cut':
                // 선택된 텍스트를 클립보드에 복사하고 삭제
                if (selectedText) {
                    try {
                        await navigator.clipboard.writeText(selectedText);
                        view.dispatch({
                            changes: { from: selection.from, to: selection.to, insert: '' }
                        });
                        console.log('✅ 잘라내기 완료:', selectedText.length, '자');
                    } catch (error) {
                        console.error('❌ 잘라내기 실패:', error);
                    }
                }
                setContextMenu(null);
                return;

            case 'paste':
                // 클립보드에서 텍스트를 가져와 붙여넣기
                try {
                    const clipboardText = await navigator.clipboard.readText();
                    if (clipboardText) {
                        view.dispatch({
                            changes: { from: selection.from, to: selection.to, insert: clipboardText }
                        });
                        console.log('✅ 붙여넣기 완료:', clipboardText.length, '자');
                    }
                } catch (error) {
                    console.error('❌ 붙여넣기 실패:', error);
                }
                setContextMenu(null);
                return;

            case 'format':
                if (selectedText) {
                    newText = format(selectedText, buildFormatOptions());
                } else {
                    const currentFullQuery = view.state.doc.toString();
                    const formattedFullQuery = format(currentFullQuery, buildFormatOptions());
                    setQueryRef.current(formattedFullQuery);
                    setContextMenu(null);
                    return;
                }
                break;
            case 'uppercase':
                newText = selectedText.toUpperCase();
                break;
            case 'lowercase':
                newText = selectedText.toLowerCase();
                break;
            case 'toggleComment':
                toggleComment(view);
                setContextMenu(null);
                return;
            default:
                break;
        }

        if (newText !== selectedText || (action === 'format' && selectedText)) {
            view.dispatch({
                changes: { from: replacementFrom, to: replacementTo, insert: newText }
            });
        }
        setContextMenu(null);
    }, [setQuery]);

    // 외부(탐색창 아이콘 바 등)에서 window 이벤트로 컨텍스트 액션 트리거 가능
    useEffect(() => {
        const onAction = (e) => {
            const action = e?.detail?.action;
            if (action) handleContextMenuAction(action);
        };
        window.addEventListener('livesql-editor-action', onAction);
        return () => window.removeEventListener('livesql-editor-action', onAction);
    }, [handleContextMenuAction]);

    const customDomEventHandlers = EditorView.domEventHandlers({
        contextmenu: (event, view) => {
            event.preventDefault();

            // 현재 선택 영역 확인
            const selection = view.state.selection.main;
            const hasSelection = selection.from !== selection.to;
            const selectedText = hasSelection ? view.state.doc.sliceString(selection.from, selection.to) : '';

            setContextMenu({
                // 메뉴는 position: fixed — 화면 확대(utils/appZoom) 중이면 마우스 좌표를 CSS px 로
                x: toCssPx(event.clientX),
                y: toCssPx(event.clientY),
                visible: true,
                hasSelection: hasSelection,
                selectedText: selectedText
            });
            return true;
        },
        mousedown: (event, view) => {
            // 컨텍스트 메뉴가 열려있을 때만 처리
            if (contextMenu && contextMenu.visible) {
                if (event.target && event.target.closest('.context-menu')) {
                    return false;
                }
                setContextMenu(null);
                return false; // 컨텍스트 메뉴 닫기만 하고 다른 이벤트는 처리하지 않음
            }
            // 일반적인 마우스 다운은 기본 동작을 허용 (텍스트 선택 포함)
            return false;
        }
    });

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (contextMenu && contextMenu.visible) {
                // 컨텍스트 메뉴 자체를 클릭한 경우 무시
                if (event.target.closest('.context-menu')) {
                    return;
                }
                // 그 외 모든 클릭은 메뉴 닫기
                setContextMenu(null);
            }
        };

        if (contextMenu && contextMenu.visible) {
            // mousedown 대신 click 이벤트 사용하여 더 안정적으로 감지
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('click', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('click', handleClickOutside);
        };
    }, [contextMenu]);

    // 커서가 있는 문장 — 나누기·고르기는 utils/statementAtCursor 한 곳(주석·따옴표를 안다).
    // 예전 사본은 주석을 몰라 `-- it's` 하나가 문장 경계를 통째로 놓쳤다.
    const getCurrentStatementWithIndex = useCallback(() => {
        if (!viewRef.current) return null;
        const view = viewRef.current;
        const sel = view.state.selection.main;
        const selectedText = view.state.doc.sliceString(sel.from, sel.to);
        // 선택된 텍스트가 있으면 그것을 반환 (index는 -1로 표시)
        if (selectedText.trim()) return { text: selectedText.trim(), index: -1 };
        const st = statementAtCursor(view.state.doc.toString(), sel.head);
        return st ? { text: st.text, index: st.index } : null;
    }, []);

    const getCurrentStatementForExecution = useCallback(() => {
        const st = getCurrentStatementWithIndex();
        return st ? st.text : null;
    }, [getCurrentStatementWithIndex]);

    const handleExecuteInternal = useCallback(() => {
        // 새로운 context-aware handler가 있으면 사용
        if (onExecuteWithContext) {
            const statementInfo = getCurrentStatementWithIndex();

            if (statementInfo) {
                console.log(`[SQLEditor] Executing statement ${statementInfo.index}: "${statementInfo.text}"`);
                onExecuteWithContext(statementInfo.text, statementInfo.index);
            } else if (viewRef.current && viewRef.current.state.doc.toString().trim() === "") {
                onExecuteWithContext("", 0);
            } else {
                onExecuteWithContext("", -1);
            }
            return;
        }

        // 기존 handler 사용 (fallback)
        if (!onExecute) {
            return;
        }

        const statementToExecute = getCurrentStatementForExecution();

        if (statementToExecute) {
            onExecute(statementToExecute);
        } else if (statementToExecute === null && viewRef.current && viewRef.current.state.doc.toString().trim() === "") {
            onExecute("");
        } else if (statementToExecute === null) {
            onExecute();
        }
    }, [onExecute, onExecuteWithContext, getCurrentStatementForExecution, getCurrentStatementWithIndex]);

    const handleExecutePlanInternal = useCallback(() => {
        if (!onExecutePlan) return;
        const statementForPlan = getCurrentStatementForExecution();
        if (statementForPlan) {
            onExecutePlan(statementForPlan);
        }
    }, [onExecutePlan, getCurrentStatementForExecution]);

    // keymap closure 안의 handler 가 항상 최신 useCallback 을 가리키도록 ref sync.
    // EditorView 가 mount 시 한 번만 빌드되어, 직접 reference 잡아두면 stale 됨.
    useEffect(() => {
        handleExecuteInternalRef.current = handleExecuteInternal;
    }, [handleExecuteInternal]);
    useEffect(() => {
        handleExecutePlanInternalRef.current = handleExecutePlanInternal;
    }, [handleExecutePlanInternal]);

    // Helper function to get word at cursor for CodeMirror 6
    const getWordAtCursorCM6 = useCallback((view) => {
        if (!view) return '';
        const { state } = view;
        const { from, to } = state.selection.main;
        let word = '';

        if (from === to) {
            const pos = from;
            const tree = syntaxTree(state);
            let node = tree.resolveInner(pos, -1);
            
            if (node.name === 'Identifier' || node.name === 'QuotedIdentifier') {
                word = state.doc.sliceString(node.from, node.to);
            } else {
                if (pos > 0) {
                    node = tree.resolveInner(pos -1, -1);
                    if (node.name === 'Identifier' || node.name === 'QuotedIdentifier') {
                         word = state.doc.sliceString(node.from, node.to);
                    }
                }
            }

        } else {
            word = state.doc.sliceString(from, to);
        }
        
        if (word.startsWith('"') && word.endsWith('"')) {
            word = word.substring(1, word.length - 1);
        }

        return word.trim();
    }, []);

    /**
     * F4 는 **한정된 이름 전체**를 넘긴다 — `sh.sales` 의 어디에 커서가 있든 `sh.sales`.
     * 규칙은 `utils/sqlIdentifier` 한 곳이다(메뉴 › 실행 경로와 같은 것을 써야 결과가 갈리지
     * 않는다). 주석 조회(F2)는 컬럼명 한 개를 봐야 하므로 `getWordAtCursorCM6` 그대로다.
     */
    const handleF4 = useCallback((view) => {
        const word = qualifiedNameAtCursor(view) || getWordAtCursorCM6(view);
        if (word && onViewSchemaInfoByEditor) {
            onViewSchemaInfoByEditor(word);
        }
        return true;
    }, [getWordAtCursorCM6, onViewSchemaInfoByEditor]);

    // 주석 조회: 커서 위치의 단어에 대한 테이블/컬럼 주석 찾기
    const lookupComment = useCallback((word) => {
        if (!word) return null;
        const upper = word.toUpperCase();

        // 1) 테이블 주석 매칭
        const tableComment = tableComments[upper];
        if (tableComment) {
            return { type: t('editor:comment.typeTable'), name: upper, comment: tableComment };
        }

        // 2) 컬럼 주석 매칭 (모든 테이블에서 검색)
        for (const [tableName, cols] of Object.entries(columnComments)) {
            if (cols[upper]) {
                const displayTable = tableName.includes('.') ? tableName.split('.')[1] : tableName;
                return { type: t('editor:comment.typeColumn'), name: `${displayTable}.${upper}`, comment: cols[upper] };
            }
        }
        return null;
    }, [tableComments, columnComments, t]);

    // 사용자 주석(2026-10-29, docs/features/32-user-comment.md) — 이 편집기의 연결에 켜진 정의가 있으면 F2 가 값의 뜻도 찾는다
    const ucmtDefs = useSelector(state => (state.userComments && state.userComments.defs) || []);
    const userCommentOn = userCommentActiveFor(ucmtDefs, connectionId);
    const tooltipTimerRef = useRef(null);
    const ucmtSeqRef = useRef(0);
    const showTooltip = useCallback((view, text, ms) => {
        const coords = view.coordsAtPos(view.state.selection.main.head);
        if (!coords) return;
        setCommentTooltip({ x: coords.left, y: coords.top - 4, text });
        if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
        tooltipTimerRef.current = ms ? setTimeout(() => setCommentTooltip(null), ms) : null;
    }, []);

    /** 커서의 값 — 선택한 글, 아니면 커서가 걸친 문자열·숫자 리터럴. { value, from } */
    const valueAtCursor = useCallback((view) => {
        const { state } = view;
        const { from, to } = state.selection.main;
        if (from !== to) return { value: unquoteValue(state.doc.sliceString(from, to)), from };
        const tree = syntaxTree(state);
        for (const side of [1, -1]) {
            const node = tree.resolveInner(from, side);
            if (node && (node.name === 'String' || node.name === 'Number')) {
                return { value: unquoteValue(state.doc.sliceString(node.from, node.to)), from: node.from };
            }
        }
        return null;
    }, []);

    /** 사용자 주석을 찾아 툴팁으로 — 주석 조회 쿼리는 이때 돈다(서버) */
    const showUserComment = useCallback(async (view, value, from) => {
        const seq = ++ucmtSeqRef.current;
        const before = view.state.doc.sliceString(Math.max(0, from - 400), from);
        const column = columnBeforeValue(before);
        showTooltip(view, t('editor:comment.userLooking', { value }), 0);
        try {
            const r = await lookupUserComment(connectionId, value, column);
            if (seq !== ucmtSeqRef.current) return;
            const lines = (r.matches || []).map(m => t('editor:comment.userLine', { area: m.area, value: m.value, comment: m.comment }));
            if (lines.length) showTooltip(view, `${t('editor:comment.typeUser')}\n${lines.join('\n')}`, 6000);
            else if (r.errors && r.errors.length) showTooltip(view, t('editor:comment.userError', { message: r.errors[0].error }), 5000);
            else showTooltip(view, t('editor:comment.userNotFound', { value }), 2500);
        } catch (e) {
            if (seq !== ucmtSeqRef.current) return;
            showTooltip(view, t('editor:comment.userError', { message: (e && e.message) || '' }), 5000);
        }
    }, [connectionId, showTooltip, t]);

    // F2 키: 커서 위치 단어의 주석 툴팁 표시 — 값(문자열·숫자)이면 사용자 주석, 이름이면 DB 주석(없으면 사용자 주석에서도 찾는다)
    const handleCommentTooltip = useCallback((view) => {
        if (!showCommentsRef.current) return true;   // 「주석 보기」를 끈 상태 — F2 를 먹되 띄우지 않는다
        const val = userCommentOn ? valueAtCursor(view) : null;
        if (val && val.value) { showUserComment(view, val.value, val.from); return true; }
        const word = getWordAtCursorCM6(view);
        const result = lookupComment(word);
        if (result) {
            showTooltip(view, `[${result.type}] ${result.name}\n${result.comment}`, 3000);
        } else if (word && userCommentOn) {
            showUserComment(view, word, view.state.selection.main.from);
        } else if (word) {
            showTooltip(view, t('editor:comment.notFound', { word }), 2000);
        }
        return true;
    }, [getWordAtCursorCM6, lookupComment, t, userCommentOn, valueAtCursor, showUserComment, showTooltip]);

    // F2 · F4 도 실행 단축키처럼 ref 로 부른다 — 편집기는 처음 한 번만 만들어져, 함수를 바로 넣으면 그때의 것(주석을 받기 전의 빈 목록)을
    // 계속 불러 「주석 정보가 없습니다」 만 떴다(2026-10-29 신고).
    const handleF4Ref = useRef(handleF4);
    handleF4Ref.current = handleF4;
    const handleCommentTooltipRef = useRef(handleCommentTooltip);
    handleCommentTooltipRef.current = handleCommentTooltip;

    const handleNewTabKey = useCallback(() => {
        if (onNewTab) {
            onNewTab();
        }
        return true;
    }, [onNewTab]);

    /** 커서가 편집기 안에 있는가 — 미니맵을 보일지 정한다. */
    const [editorFocused, setEditorFocused] = useState(false);

    const handleFocusEvent = useCallback((event, view) => {
        // 미니맵은 **커서가 편집기에 있을 때만** 보인다(사용자 요청 2026-09-20).
        // 평소에는 코드 옆에 회색 띠가 늘 붙어 있어 시선을 끌었다 — 쓰는 순간에만 필요하다.
        setEditorFocused(true);
        if (onFocus) onFocus();
    }, [onFocus]);

    const handleBlurEvent = useCallback((event, view) => {
        setEditorFocused(false);
        if (onBlur) onBlur();
    }, [onBlur]);

    // 워터마크 위치 설정은 CSS의 position: sticky로 처리되므로 더 이상 JS 기반 좌표 계산이 필요하지 않습니다.

    // 에디터 초기화 useEffect - dependency를 최소화
    useEffect(() => {
        if (editorRefInternal.current && !viewRef.current) {
            // defaultKeymap에서 Tab 관련 바인딩을 필터링
            const filteredDefaultKeymap = defaultKeymap.filter(k => k.key !== 'Tab' && k.key !== 'Shift-Tab');
            // completionKeymap에서 Tab 관련 바인딩을 필터링
            const filteredCompletionKeymap = completionKeymap.filter(k => k.key !== 'Tab');

            // F3 · Ctrl+G — 툴바 검색어로 이동한다. 단 Ctrl+H 패널이 열려 있으면 그 패널의 검색어를 따른다
            // (false 를 돌려주면 아래 searchKeymap 이 받는다)
            const navFind = (view, dir) => {
                if (searchPanelOpen(view.state) || !onFindNavRef.current) return false;
                onFindNavRef.current(dir);
                return true;
            };

            const customKeyBinding = Prec.high(keymap.of([
                // 찾기는 편집기 툴바의 검색 창 하나로 한다(onFind). 바꾸기·정규식이 필요할 때만 Ctrl+H 로
                // CodeMirror 패널을 연다. onFind 를 받지 않은 편집기(PL/SQL 창의 호출문 칸·읽기 전용 복제)는 패널 그대로다.
                {
                    key: 'Mod-f',
                    run: (view) => {
                        if (!onFindRef.current) return openSearchPanel(view);
                        const sel = view.state.selection.main;
                        const text = sel.empty ? '' : view.state.sliceDoc(sel.from, sel.to);
                        // 여러 줄 선택은 검색어로 쓰지 않는다 — 한 줄 입력칸에 담기지 않는다
                        onFindRef.current(text.includes('\n') ? '' : text);
                        return true;
                    },
                    preventDefault: true
                },
                {
                    key: 'Mod-h',
                    run: openReplacePanel, // 메뉴 편집 › 바꾸기와 같은 함수
                    preventDefault: true
                },
                { key: 'F3', run: (view) => navFind(view, 'next'), shift: (view) => navFind(view, 'prev') },
                { key: 'Mod-g', run: (view) => navFind(view, 'next'), shift: (view) => navFind(view, 'prev') },
                {
                    key: 'Ctrl-Enter',
                    mac: 'Cmd-Enter',
                    run: () => { handleExecuteInternalRef.current?.(); return true; },
                    preventDefault: true
                },
                {
                    key: 'Ctrl-E',
                    mac: 'Cmd-E',
                    run: () => { handleExecutePlanInternalRef.current?.(); return true; },
                    preventDefault: true
                },
                {
                    key: 'F4',
                    run: (view) => handleF4Ref.current(view),
                    preventDefault: true
                },
                {
                    key: 'F2',
                    run: (view) => handleCommentTooltipRef.current(view),
                    preventDefault: true
                },
                {
                    key: 'Alt-n',
                    mac: 'Alt-n',
                    run: handleNewTabKey,
                    preventDefault: true
                },
                { key: "Tab", run: editorTabKey, shift: editorShiftTabKey },
                {
                    key: "Enter",
                    run: (view) => {
                        // 자동 들여쓰기 없이 단순 줄바꿈만 수행
                        view.dispatch(view.state.replaceSelection("\n"));
                        return true;
                    },
                    preventDefault: true
                }
            ]));

            const priorityKeymap = Prec.highest(customKeyBinding);

            // CodeMirror 6 선택 영역 강조 테마 및 스크롤바 스타일 - 정확한 선택자 사용
            const selectionTheme = EditorView.theme({
                // 텍스트 선택 영역 (포커스 시) - 명확한 파란색
                '&.cm-focused .cm-selectionBackground': {
                    backgroundColor: '#0078D4',
                    borderRadius: '1px'
                },
                // 텍스트 선택 영역 (비포커스 시)
                '.cm-selectionBackground': {
                    backgroundColor: '#CCE7FF',
                    borderRadius: '1px'
                },
                // 활성 라인 (커서가 있는 줄) - 선택 영역과 구분되게 매우 연하게
                '&.cm-focused .cm-activeLine': {
                    backgroundColor: 'rgba(0, 120, 212, 0.08)'
                },
                '.cm-activeLine': {
                    backgroundColor: 'rgba(0, 0, 0, 0.02)'
                },
                // 커서 스타일
                '&.cm-focused .cm-cursor': {
                    borderLeftWidth: '2px',
                    borderLeftColor: '#0078D4',
                    borderLeftStyle: 'solid'
                },
                '.cm-cursor': {
                    borderLeftWidth: '1px',
                    borderLeftColor: '#999',
                    borderLeftStyle: 'solid'
                },
                // 스크롤바 스타일 개선
                '.cm-scroller': {
                    overflow: 'auto'
                },
                // 스크롤바 스타일링 (Webkit 기반 브라우저)
                '.cm-scroller::-webkit-scrollbar': {
                    width: '12px'
                },
                '.cm-scroller::-webkit-scrollbar-track': {
                    background: '#f1f1f1',
                    borderRadius: '6px'
                },
                '.cm-scroller::-webkit-scrollbar-thumb': {
                    background: '#c1c1c1',
                    borderRadius: '6px'
                },
                '.cm-scroller::-webkit-scrollbar-thumb:hover': {
                    background: '#a8a8a8'
                },
                // 검색 패널 스타일
                '.cm-search': {
                    fontSize: '12px'
                },
                '.cm-textfield': {
                    fontSize: '12px'
                },
                '.cm-button': {
                    fontSize: '12px'
                }
            }, {dark: false});

            const extensions = [
                highlightField,
                bookmarkField,
                bookmarkLineDecorations,
                // gutter 좌→우 순서: 결과탭 점프 (↳) → 책갈피 (●) → line number.
                // gutter() 가 추가되는 순서대로 좌측부터 배치되므로, linkedResultGutter 를 먼저 등록.
                readOnlyCompartmentRef.current.of(readOnlyExtensions(readOnly)),
                // 결과탭 점프·문장 구분선은 **만들 때** 정한다(읽기 전용 복제본에는 없다).
                // 읽기 전용 여부는 위 Compartment 가 나중에도 따라간다.
                ...(readOnly ? [] : [
                    linkedResultField,
                    createLinkedResultGutter(() => onActivateResultTabRef.current),
                    statementSeparatorField,
                ]),
                createBookmarkGutter(),
                lineNumbers({
                    domEventHandlers: {
                        mousedown(view, line) {
                            return toggleBookmarkAt(view, line);
                        },
                    },
                }),
                highlightActiveLineGutter(),
                highlightActiveLine(),
                scrollPastEnd(),
                history(),
                drawSelection({
                    drawRangeCursor: false, // 범위 선택에서 커서 표시 비활성화
                    cursorBlinkRate: 1200   // 커서 깜빡임 속도
                }),
                // scrollPastEnd(), // 과도한 패딩을 방지하기 위해 일시적으로 비활성화
                Prec.highest(selectionTheme), // 최고 우선순위로 커스텀 선택 테마 추가
                // rectangularSelection은 다중 selection을 만들므로 허용 필요
                EditorState.allowMultipleSelections.of(true),
                // 컬럼(사각형) 선택: 모드 ON이면 일반 드래그로, OFF면 Alt+드래그로 활성화
                rectangularSelection({
                    eventFilter: (event) => columnSelectionRef.current || event.altKey
                }),
                // Alt 누르면 십자 커서 표시 (시각적 피드백)
                crosshairCursor(),
                // indentOnInput(), // 엔터 키 누를 때 자동 들여쓰기 비활성화
                // 들여쓰기 한 단계 = 공백 4칸 — 선택 없는 Tab 이 넣는 칸 수와 같게(Tab/Shift+Tab 의 줄 들여쓰기가 이 값을 쓴다)
                indentUnit.of('    '),
                syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
                bracketMatching(),
                closeBrackets(),
                search(), // 검색 기능 추가
                // 검색 패널 입력칸의 브라우저 자동완성을 끈다 — name="search" 라 브라우저가 예전에 친 단어를
                // 밝은 팝업으로 띄워 다크모드에서 튄다(패널은 효과로 열리므로 효과가 있는 갱신에서만 본다)
                EditorView.updateListener.of((u) => {
                    if (!u.transactions.some(tr => tr.effects.length)) return;
                    u.view.dom.querySelectorAll('.cm-search .cm-textfield:not([autocomplete])')
                        .forEach(el => el.setAttribute('autocomplete', 'off'));
                }),
                keymap.of([
                    ...filteredDefaultKeymap, // 필터링된 defaultKeymap 사용
                    ...historyKeymap,
                    ...filteredCompletionKeymap, // 필터링된 completionKeymap 사용
                    ...searchKeymap // 검색 키맵 추가 (Ctrl-F, Ctrl-G 등)
                ]),
                sql(),
                priorityKeymap, // Tab 처리가 포함된 priorityKeymap
                autocompletion({
                    activateOnTyping: true,
                    override: [completionOverride]
                }),
                customDomEventHandlers,
                EditorView.domEventHandlers({
                    focus: handleFocusEvent,
                    blur: handleBlurEvent,
                    paste: async (event, view) => {
                        console.log('📋 [Paste Event] 붙여넣기 시작');

                        // ⚠️ 중요: event.preventDefault()를 먼저 호출하고 selection을 미리 저장
                        event.preventDefault();

                        // 현재 selection과 문서 상태를 미리 저장 (비동기 처리 전)
                        const initialState = view.state;
                        const initialSelection = initialState.selection.main;
                        const initialDocLength = initialState.doc.length;

                        console.log('📋 [Initial] 문서 길이:', initialDocLength, 'selection:', initialSelection.from, '-', initialSelection.to);

                        try {
                            // 클립보드에서 텍스트 가져오기
                            let pastedText = null;

                            // 1. 이벤트에서 직접 가져오기 (빠름, 짧은 텍스트용)
                            const clipboardData = event.clipboardData || window.clipboardData;
                            console.log('📋 [Step 1] clipboardData 존재:', !!clipboardData);

                            if (clipboardData) {
                                const eventText = clipboardData.getData('text');
                                console.log('📋 [Step 1] getData 결과 - 타입:', typeof eventText, '길이:', eventText?.length || 0);

                                if (eventText && eventText.length > 0) {
                                    pastedText = eventText;
                                    console.log('✅ [Step 1] 이벤트에서 텍스트 가져옴:', pastedText.length, '자');
                                } else {
                                    console.log('⚠️ [Step 1] 이벤트에서 텍스트 없음 (빈 문자열 또는 null)');
                                }
                            }

                            // 2. 클립보드 API 사용 (큰 텍스트 또는 이벤트 실패 시)
                            if (!pastedText) {
                                console.log('📋 [Step 2] Clipboard API 시도 - 이벤트 실패했거나 빈 텍스트');
                                console.log('📋 [Step 2] navigator.clipboard 존재:', !!navigator.clipboard);
                                console.log('📋 [Step 2] readText 지원:', !!(navigator.clipboard && navigator.clipboard.readText));

                                if (navigator.clipboard && navigator.clipboard.readText) {
                                    try {
                                        console.log('📋 [Step 2] Clipboard API readText() 호출 중...');
                                        const clipboardText = await navigator.clipboard.readText();
                                        console.log('📋 [Step 2] readText() 결과 - 타입:', typeof clipboardText, '길이:', clipboardText?.length || 0);

                                        if (clipboardText && clipboardText.length > 0) {
                                            pastedText = clipboardText;
                                            console.log('✅ [Step 2] Clipboard API에서 텍스트 가져옴:', pastedText.length, '자');
                                        } else {
                                            console.log('⚠️ [Step 2] Clipboard API 결과가 빈 문자열');
                                        }
                                    } catch (clipboardError) {
                                        console.error('❌ [Step 2] Clipboard API 오류:', clipboardError);
                                        return true; // 이미 preventDefault 했으므로 true 반환
                                    }
                                } else {
                                    console.log('⚠️ [Step 2] Clipboard API 미지원');
                                }
                            }

                            console.log('📋 [Final Check] 최종 pastedText:', pastedText ? `${pastedText.length}자` : 'null');

                            if (pastedText && pastedText.length > 0) {
                                console.log('📋 [Paste] 원본 텍스트 길이:', pastedText.length);
                                console.log('📋 [Paste] 원본 텍스트 미리보기:', pastedText.substring(0, 100) + '...');

                                // ========================================
                                // 원본 그대로 붙여넣기 (기본 정책)
                                // ========================================
                                // 아래 정규식 처리는 주석 처리됨 (필요시 원복 가능)
                                //
                                // 이전에 적용했던 정제 로직:
                                // - NULL 문자 제거 (\x00): 데이터베이스 오류 방지용
                                // - Zero-width 문자 제거 ([\u200B-\u200D\uFEFF]): 파싱 오류 방지용
                                //
                                // const cleanedText = pastedText
                                //     .replace(/\x00/g, '')
                                //     .replace(/[\u200B-\u200D\uFEFF]/g, '');

                                const cleanedText = pastedText; // 원본 그대로 사용
                                console.log('📋 [Paste] 원본 그대로 사용');

                                // CodeMirror replaceSelection 사용
                                try {
                                    // 현재 view의 최신 상태 가져오기
                                    const currentState = view.state;
                                    const currentSelection = currentState.selection.main;
                                    const docLength = currentState.doc.length;

                                    console.log('📋 [Insert] 현재 문서 길이:', docLength);
                                    console.log('📋 [Insert] 현재 selection:', currentSelection.from, '-', currentSelection.to);

                                    // CodeMirror의 replaceSelection을 사용 (더 안전)
                                    // from === to === docLength인 경우를 처리하기 위해 수동으로 범위 조정
                                    let insertFrom = currentSelection.from;
                                    let insertTo = currentSelection.to;

                                    // 문서 끝을 넘어가는 경우 안전하게 조정
                                    if (insertFrom > docLength) insertFrom = docLength;
                                    if (insertTo > docLength) insertTo = docLength;

                                    // from과 to가 같고 문서 끝인 경우 (커서가 문서 끝에 있음)
                                    if (insertFrom === insertTo && insertFrom === docLength) {
                                        console.log('📋 [Insert] 문서 끝에 추가');
                                        // 문서 끝에 추가할 때는 selection을 자동 계산하도록 함
                                        const transaction = view.state.update({
                                            changes: { from: insertFrom, insert: cleanedText },
                                            // selection 자동 계산 (텍스트 삽입 후 끝으로 이동)
                                            userEvent: 'input.paste'
                                        });
                                        view.dispatch(transaction);
                                    } else {
                                        console.log('📋 [Insert] 선택 영역 대체:', insertFrom, '-', insertTo);
                                        // 일반적인 삽입/대체
                                        const transaction = view.state.update({
                                            changes: { from: insertFrom, to: insertTo, insert: cleanedText },
                                            userEvent: 'input.paste'
                                        });
                                        view.dispatch(transaction);
                                    }

                                    console.log('✅ [Insert] 텍스트 삽입 완료');
                                } catch (insertError) {
                                    console.error('❌ [Insert] 삽입 중 오류:', insertError);
                                    console.error('❌ [Insert] 오류 상세:', insertError.stack);
                                }

                                return true; // 이벤트 처리됨
                            }

                            console.log('⚠️ [Paste] pastedText가 없음');
                            return true; // 이미 preventDefault 했으므로 true 반환
                        } catch (error) {
                            console.error('❌ [Paste] 오류 발생:', error);
                            console.error('❌ [Paste] 오류 스택:', error.stack);
                            return true; // 이미 preventDefault 했으므로 true 반환
                        }
                    }
                }),
                EditorView.updateListener.of((update) => {
                    if (update.docChanged && !skipNextUpdate.current) {
                        const newContent = update.state.doc.toString();
                        setQueryRef.current(newContent);
                        setCurrentEditorContent(newContent);
                    }
                    // 커서 이동 시 주석 툴팁 즉시 닫기
                    if (update.selectionSet) {
                        setCommentTooltip(null);
                    }
                    skipNextUpdate.current = false;
                })
            ];

            // 팝업 창 분리 시에도 올바른 document 사용 (마운트된 DOM의 ownerDocument)
            const ownerDoc = editorRefInternal.current.ownerDocument || document;

            const view = new EditorView({
                state: EditorState.create({
                    doc: query,
                    extensions: extensions,
                }),
                parent: editorRefInternal.current,
                root: ownerDoc
            });

            viewRef.current = view;
            // 미니맵처럼 **뷰가 있어야 붙을 수 있는** 것들에게 알린다. 이 신호가 없으면
            // "마운트 시 뷰가 없었다" 는 이유로 영영 초기화되지 않는다(수직분할로 새로 뜬
            // 편집기의 미니맵이 빈 채 남아 '깨져' 보였다 — 신고 2026-09-18).
            setViewReady(v => v + 1);

            if (editorRef) {
                editorRef.current = viewRef.current;
                // App 의 handleExecuteCurrent(툴바 실행 버튼·메뉴)는 이 ref 에서
                // handleExecuteInternal 을 찾는다. EditorView 에는 그런 메서드가 없어
                // 조용한 no-op 이었다 — insertTextAtCursor 등이 EditorView 계약
                // (state/dispatch)에 의존하므로 view 를 유지한 채 메서드만 얹는다.
                // ref 를 경유해야 keymap 과 동일하게 항상 최신 콜백을 가리킨다.
                editorRef.current.handleExecuteInternal = () => handleExecuteInternalRef.current?.();
            }

            return () => {
                view.destroy();
                viewRef.current = null;
            };
        }
    }, []);

    // 쿼리 업데이트 useEffect
    useEffect(() => {
        if (viewRef.current && viewRef.current.state.doc.toString() !== query) {
            skipNextUpdate.current = true;
            viewRef.current.dispatch({
                changes: {
                    from: 0,
                    to: viewRef.current.state.doc.length,
                    insert: query
                }
            });
        }
    }, [query]);

    // 외부 하이라이트 적용 (실행계획↔에디터 연동)
    useEffect(() => {
        const view = viewRef.current;
        if (!view) return;

        // 기존 하이라이트 마크 CSS 클래스 제거 후 재적용
        const decorations = [];
        const docLen = view.state.doc.length;
        for (const range of highlightRanges) {
            if (range.from >= 0 && range.to <= docLen && range.from < range.to) {
                const className = range.type === 'access' ? 'cm-highlight-access'
                    : range.type === 'filter' ? 'cm-highlight-filter'
                    : 'cm-highlight-table';
                decorations.push(
                    Decoration.mark({ class: className, attributes: { title: range.label || '' } })
                        .range(range.from, range.to)
                );
            }
        }
        decorations.sort((a, b) => a.from - b.from);

        view.dispatch({
            effects: setHighlightsEffect.of(decorations),
        });

        // 첫 번째 하이라이트 위치로 스크롤 — 문서 안일 때만(강조 범위가 문서보다 늦게 바뀌면 「Selection points outside of document」 로
        // 화면이 죽었다: SQL 빌더에서 되돌리기·지우기로 SQL 이 짧아진 직후, 2026-10-19 신고)
        if (highlightRanges.length > 0 && highlightRanges[0].from >= 0 && highlightRanges[0].from <= docLen) {
            view.dispatch({
                selection: { anchor: highlightRanges[0].from },
                scrollIntoView: true,
            });
        }
    }, [highlightRanges]);

    // ============================================
    // 미니맵
    // ============================================
    const minimapRef = useRef(null);
    const minimapAnimRef = useRef(null);
    // viewReady 는 위(결과탭 연결 표시 효과보다 앞)에서 선언한다 — 그 효과가 의존한다

    // **readOnly 가 바뀌면 따라간다.** 편집기는 한 번만 만들어지고(effect 의존성 []) 예전엔 그때의
    // readOnly 가 끝까지 갔다 — PL/SQL 편집기는 객체를 불러오는 동안 readOnly 로 두는데, 창을 열고
    // 처음 누른 것이 객체면 편집기가 「로딩 중 = 읽기 전용」으로 태어나 불러온 뒤에도 고칠 수가 없었다
    // (먼저 새 익명 블록을 누르면 멀쩡해서 "되기도 한다" 로 보였다. 실측 2026-09-29).
    useEffect(() => {
        const view = viewRef.current;
        if (!view) return;
        view.dispatch({ effects: readOnlyCompartmentRef.current.reconfigure(readOnlyExtensions(readOnly)) });
    }, [readOnly, viewReady]);

    const drawMinimap = useCallback(() => {
        const canvas = minimapRef.current;
        const view = viewRef.current;
        if (!canvas || !view) return;

        const isDarkMode = document.documentElement.getAttribute('data-theme') === 'dark';

        const dpr = window.devicePixelRatio || 1;
        const w = canvas.offsetWidth;
        const h = canvas.offsetHeight;
        // 패널이 접히는/전환 애니메이션 중간처럼 일시적으로 0 크기일 때 canvas.width/height 를
        // 0 으로 리셋해버리면(캔버스 스펙상 크기 대입은 항상 비트맵을 지운다) 그 뒤 정상 크기로
        // 돌아와도 다시 그려질 때까지 빈 캔버스가 CSS 박스에 늘어나 보인다(미니맵이 하얗게 깨짐).
        // 크기가 없을 때는 그리기를 건너뛰고 이전 비트맵을 그대로 둔다 — ResizeObserver 가
        // 정상 크기가 되는 즉시 다시 호출해준다.
        if (w <= 0 || h <= 0) return;
        canvas.width = w * dpr;
        canvas.height = h * dpr;
        const ctx = canvas.getContext('2d');
        ctx.scale(dpr, dpr);
        ctx.clearRect(0, 0, w, h);

        const doc = view.state.doc;
        const lineCount = doc.lines;
        if (lineCount === 0) return;

        // 한 줄은 최대 3px — 줄 수가 달라도 같은 축척(utils/minimapScale, 실행계획 보기와 같다)
        const lineH = minimapLineHeight(h, lineCount);
        const maxLineLen = 200;

        // 코드 라인 렌더
        for (let i = 1; i <= lineCount; i++) {
            const lineText = doc.line(i).text;
            const len = Math.min(lineText.length, maxLineLen);
            if (len === 0) continue;
            const indent = lineText.length - lineText.trimStart().length;
            const x = (indent / maxLineLen) * w;
            const barW = Math.max(1, (len / maxLineLen) * w * 0.8);
            ctx.fillStyle = isDarkMode ? 'rgba(160, 170, 180, 0.5)' : 'rgba(100, 120, 150, 0.3)';
            ctx.fillRect(x, (i - 1) * lineH, barW, Math.max(1, lineH - 0.5));
        }

        // 책갈피 위치 표시
        try {
            const marks = view.state.field(bookmarkField);
            const iter = marks.iter();
            while (iter.value) {
                const lineNum = doc.lineAt(iter.from).number;
                const y = (lineNum - 1) * lineH;
                ctx.fillStyle = isDarkMode ? 'rgba(64, 169, 255, 0.8)' : 'rgba(33, 150, 243, 0.6)';
                ctx.fillRect(0, y, 3, Math.max(2, lineH));
                ctx.fillStyle = isDarkMode ? 'rgba(64, 169, 255, 0.25)' : 'rgba(33, 150, 243, 0.15)';
                ctx.fillRect(0, y, w, Math.max(2, lineH));
                iter.next();
            }
        } catch (e) { /* bookmarkField 없으면 무시 */ }

        // 뷰포트 영역
        const scrollDOM = view.scrollDOM;
        const totalH = scrollDOM.scrollHeight;
        const viewTop = scrollDOM.scrollTop;
        const viewH = scrollDOM.clientHeight;

        const vp = minimapViewport({ height: h, lineCount, scrollTop: viewTop, scrollHeight: totalH, clientHeight: viewH });
        if (vp) {
            ctx.fillStyle = isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 120, 215, 0.12)';
            ctx.fillRect(0, vp.top, w, vp.height);
            ctx.strokeStyle = isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 120, 215, 0.3)';
            ctx.lineWidth = 1;
            ctx.strokeRect(0.5, vp.top + 0.5, w - 1, vp.height - 1);
        }
    }, []);

    // 미니맵 클릭/드래그 → 에디터 스크롤
    const handleMinimapMouse = useCallback((e) => {
        const canvas = minimapRef.current;
        const view = viewRef.current;
        if (!canvas || !view) return;

        const rect = canvas.getBoundingClientRect();
        // 그리기와 같은 축척으로 — 다르면 누른 자리와 옮겨 가는 자리가 어긋난다
        view.scrollDOM.scrollTop = minimapScrollTarget({
            y: e.clientY - rect.top, height: rect.height, lineCount: view.state.doc.lines,
            scrollHeight: view.scrollDOM.scrollHeight, clientHeight: view.scrollDOM.clientHeight,
        });
    }, []);

    const handleMinimapDrag = useCallback((e) => {
        e.preventDefault();
        handleMinimapMouse(e);
        // 분리 창에서도 그 창의 문서로 받는다(utils/dragListen) — 본창 document 에 걸면 끌기가 안 끝나 본창의 마우스 이동이 이 편집기를 굴렸다
        listenDrag(e, (ev) => handleMinimapMouse(ev));
    }, [handleMinimapMouse]);

    // 미니맵 실시간 갱신 — 스크롤 + 문서 변경 시 즉시 다시 그리기
    useEffect(() => {
        const view = viewRef.current;
        const canvas = minimapRef.current;
        if (!view || !minimap) return;

        let rafId = null;
        const scheduleRedraw = () => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(drawMinimap);
        };

        // 스크롤 이벤트
        const scrollDOM = view.scrollDOM;
        scrollDOM.addEventListener('scroll', scheduleRedraw, { passive: true });

        // 문서 변경 감지 (폴백 — 타이핑 시)
        const interval = setInterval(scheduleRedraw, 2000);

        // 패널 크기 변경(스플릿뷰 드래그·사이드바 접기/펴기·창 크기 조절) 시 즉시 재드로잉.
        // drawMinimap 은 canvas.offsetWidth/Height 로 매번 canvas.width/height(내부 비트맵)를
        // 다시 잡는데, 이 관찰이 없으면 리사이즈 직후~다음 스크롤/2초 인터벌 사이에 예전 크기로
        // 그려진 비트맵이 새 CSS 박스 크기로 그대로 늘어나 보여 미니맵이 하얗게/뭉개져 보였다
        // (이 앱은 UI 전환에 항상 애니메이션을 쓰므로 리사이즈가 실제로 자주 발생한다).
        let resizeObserver = null;
        if (canvas && typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(scheduleRedraw);
            resizeObserver.observe(canvas);
        }

        // 초기 드로잉
        scheduleRedraw();

        return () => {
            scrollDOM.removeEventListener('scroll', scheduleRedraw);
            clearInterval(interval);
            if (rafId) cancelAnimationFrame(rafId);
            if (resizeObserver) resizeObserver.disconnect();
        };
        // `viewReady` — 뷰는 다른 효과에서 만들어지므로 이 효과가 먼저 돌 수 있다.
        // 그때 그냥 빠져나오면 스크롤 구독도 ResizeObserver 도 없이 영영 방치된다.
    }, [drawMinimap, viewReady, minimap]);

    const handleFormat = useCallback(() => {
        if (!viewRef.current) return;

        const currentQuery = viewRef.current.state.doc.toString();
        const formattedQuery = format(currentQuery, buildFormatOptions());
        setQueryRef.current(formattedQuery);
    }, [setQuery]);

    return (
        <div ref={containerRef} className={`sql-editor-inner-container ${readOnly ? 'sql-editor-readonly-wrapper' : ''}`}>
            {readOnly && <div className="sql-editor-readonly-watermark">READ ONLY</div>}
            <div className="sql-editor-body">
            <div
                className={[!readOnly && watermark ? 'sql-editor-watermark-host' : '', minimap ? 'sql-editor-minimap-host' : ''].filter(Boolean).join(' ') || undefined}
                style={{ position: 'relative', flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
            >
                {/* 연결명 워터마크 — 이 SQL 이 어느 DB 로 나가는지 쓰는 동안에도 보이게(사용자 요청 2026-09-13).
                    **코드 뒤에** 깐다: 편집기 영역보다 먼저 그리고, host 클래스로 CodeMirror 배경을 투명하게 한다.
                    위에 덮으면 코드 글자에 회색 막이 씌워진다. 읽기 전용 복제본은 READ ONLY 워터마크가 가운데를 쓴다. */}
                {!readOnly && watermark && (
                    <div key={watermark} className="sql-editor-connection-watermark" aria-hidden="true">
                        <span>{watermark}</span>
                    </div>
                )}
                <div ref={editorRefInternal} className="codemirror-editor-area" style={{ flex: 1, display:'flex', flexDirection: 'column', minWidth: 0 }} />
                {/* 실행계획 보기와 같은 방식(2026-10-05) — 세로 스크롤바 대신 미니맵이 위치를 보여 주고 끌어 옮긴다.
                    **마우스를 올렸을 때(CSS :hover) 또는 커서가 편집기에 있을 때** 보인다. DOM 에서 빼지 않고 흐리게만 한다 —
                    빼 버리면 다시 들어올 때 캔버스가 새로 붙어 그릴 것이 없는 한 프레임이
                    생기고(미니맵이 빈 채 깜빡인다), 폭이 들락거려 코드가 좌우로 출렁인다. */}
                {/* 몇 줄짜리 작은 편집기(PL/SQL 편집기의 테스트 호출 칸 등)는 끈다(minimap={false}). 꺼지면 세로 스크롤바가 남는다.
                    PL/SQL 편집기의 본문은 켠다 — 프로시저 소스는 길다(사용자 요청 2026-10-05) */}
                {minimap && (
                    <canvas
                        ref={minimapRef}
                        className={`sql-editor-minimap${editorFocused ? '' : ' is-idle'}`}
                        onMouseDown={handleMinimapDrag}
                        title={t('editor:minimap.hint')}
                        aria-hidden={!editorFocused}
                    />
                )}
            </div>
            {/* 문장별 바인드 변수 — **편집기 오른쪽**에 둔다(2026-10-13). 위에 두면 가뜩이나 모자란 세로 공간을 먹는다.
                접으면 좁은 세로 띠만 남는다. 편집기와 한 줄(sql-editor-body)에 놓고 순서는 편집기 다음이다 */}
            {statements.length > 0 && statements.some(stmt => stmt.bindVars.length > 0) && (
                <div className={`bind-variables-section${isBindVariablesCollapsed ? ' collapsed' : ''}`}>
                    {/* 토글 헤더 */}
                    <div className="bind-variables-toggle-header" onClick={toggleBindVariablesSection}>
                        <span className="bind-variables-toggle-icon">
                            {isBindVariablesCollapsed ? '▶' : '▼'}
                        </span>
                        <span className="bind-variables-toggle-title">
                            {t('editor:bind.sectionTitle', { n: statements.filter(stmt => stmt.bindVars.length > 0).length })}
                        </span>
                    </div>

                    {/* 접기/펼치기 가능한 바인드 변수 영역 */}
                    {!isBindVariablesCollapsed && (
                        <div className="bind-statements-indicator">
                    {statements.map((stmt, index) => {
                        if (stmt.bindVars.length === 0) return null;

                        // 문장별 바인드 변수 키
                        const statementKey = `statement_${index}`;
                        const statementBindVariables = bindVariables[statementKey] || {};

                        const hasStoredValues = stmt.bindVars.some(varName =>
                            statementBindVariables.hasOwnProperty(varName)
                        );

                        const stmtBindVariables = {};
                        if (hasStoredValues) {
                            stmt.bindVars.forEach(varName => {
                                if (statementBindVariables.hasOwnProperty(varName)) {
                                    stmtBindVariables[varName] = statementBindVariables[varName];
                                }
                            });
                        }

                        return (
                            <div
                                key={index}
                                className="bind-variables-indicator clickable"
                                onClick={() => onEditBindVariables && onEditBindVariables(null, index)}
                                title={t('editor:bindVar.clickToEdit')}
                            >
                                <div className="bind-variables-header">
                                    <span className="bind-variables-icon">🔗</span>
                                    <span className="bind-variables-title">Statement {index + 1} Bind Variables</span>
                                    {hasStoredValues && (
                                        <span className="bind-variables-count">({Object.keys(stmtBindVariables).length} saved)</span>
                                    )}
                                    <span className="edit-hint">{t('editor:ctx.edit')}</span>
                                </div>
                                {hasStoredValues && (
                                    <div className="bind-variables-preview">
                                        {Object.entries(stmtBindVariables).map(([key, value]) => (
                                            <span key={key} className="bind-variable-preview-item">
                                                <code>:{key}</code> = <code>"{value}"</code>
                                            </span>
                                        ))}
                                    </div>
                                )}
                                {!hasStoredValues && (
                                    <div className="bind-variables-detected">
                                        <span className="bind-variables-text">
                                            Detected: {stmt.bindVars.map(v => `:${v}`).join(', ')} (not yet executed)
                                        </span>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                        </div>
                    )}
                </div>
            )}
            </div>
            {contextMenu && contextMenu.visible && (
                <ul
                    className="context-menu"
                    style={{ top: contextMenu.y, left: contextMenu.x }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <li onClick={() => handleContextMenuAction('undo')}>{t('editor:ctx.undo')}</li>
                    <li onClick={() => handleContextMenuAction('redo')}>{t('editor:ctx.redo')}</li>
                    <li className="context-menu-separator"></li>
                    <li
                        className={contextMenu.hasSelection ? '' : 'disabled'}
                        onClick={() => contextMenu.hasSelection && handleContextMenuAction('copy')}
                    >
                        {t('editor:ctx.copy')}
                    </li>
                    <li
                        className={contextMenu.hasSelection ? '' : 'disabled'}
                        onClick={() => contextMenu.hasSelection && handleContextMenuAction('cut')}
                    >
                        {t('editor:ctx.cut')}
                    </li>
                    <li onClick={() => handleContextMenuAction('paste')}>{t('editor:ctx.paste')}</li>
                    <li className="context-menu-separator"></li>
                    <li onClick={() => handleContextMenuAction('format')}>
                        {contextMenu.hasSelection ? 'Format Selection' : 'Format Query'}
                    </li>
                    <li
                        className={contextMenu.hasSelection ? '' : 'disabled'}
                        onClick={() => contextMenu.hasSelection && handleContextMenuAction('uppercase')}
                    >
                        To Uppercase
                    </li>
                    <li
                        className={contextMenu.hasSelection ? '' : 'disabled'}
                        onClick={() => contextMenu.hasSelection && handleContextMenuAction('lowercase')}
                    >
                        To Lowercase
                    </li>
                    <li
                        className={contextMenu.hasSelection ? '' : 'disabled'}
                        onClick={() => contextMenu.hasSelection && handleContextMenuAction('toggleComment')}
                    >
                        Toggle Comment
                    </li>
                </ul>
            )}
            {commentTooltip && (
                <div
                    className="comment-tooltip"
                    style={{
                        position: 'fixed',
                        left: commentTooltip.x,
                        top: commentTooltip.y,
                        transform: 'translateY(-100%)',
                        zIndex: 1000,
                    }}
                    onClick={() => setCommentTooltip(null)}
                >
                    {commentTooltip.text.split('\n').map((line, i) => (
                        <div key={i}>{line}</div>
                    ))}
                </div>
            )}
        </div>
    );
};

SQLEditor.propTypes = {
    query: PropTypes.string.isRequired,
    setQuery: PropTypes.func.isRequired,
    onExecute: PropTypes.func.isRequired,
    onExecutePlan: PropTypes.func.isRequired,
    onViewSchemaInfoByEditor: PropTypes.func.isRequired,
    schemas: PropTypes.array,
    editorRef: PropTypes.object,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onNewTab: PropTypes.func,
    bindVariables: PropTypes.object,
    onEditBindVariables: PropTypes.func,
    tableComments: PropTypes.object,
    columnComments: PropTypes.object,
    connectionId: PropTypes.string,
    dbType: PropTypes.string,
    usrid: PropTypes.string,
    defaultSchema: PropTypes.string,
    highlightRanges: PropTypes.array,
    columnSelection: PropTypes.bool,
    autocomplete: PropTypes.bool,
    showComments: PropTypes.bool,
    linkedResultTabs: PropTypes.array,
    onActivateResultTab: PropTypes.func,
    onFind: PropTypes.func,
    onFindNav: PropTypes.func,
    watermark: PropTypes.string,
};

export default SQLEditor;